import * as React from 'react'
import { useMemo, useState } from 'react'
import { Button, Drawer, FormLayoutRow, StatusBadge, Well } from '@pushly/aqe/lib/components'
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Divider, Form, notification, Select, Skeleton, Switch, Tooltip } from 'antd'
import { classnames } from '@halo/_utils/classnames'
import { simpleNotification } from '@halo/_utils/notifications'
import { simpleFormErrorNotification } from '@pushly/aqe/lib/utils'
import { AppState } from '@halo/stores/app.state'
import { Container } from 'typescript-ioc/es5'
import deepEqual from 'fast-deep-equal'
import * as randomstring from 'randomstring'
import { DomainDto } from '@halo/dtos/domain.dto'
import { DomainService } from '@halo/services/domain.service'
import { IDomainEcommConfig } from '@halo/interfaces/domain-ecomm-config'
import { SimpleTimeframe } from '@halo/components/simple-timeframe/simple-timeframe'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { asDomainSubject } from '@halo/stores/ability.state'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import { titleCase } from '@pushly/models/lib/utils/change-case'
import { Rule } from 'antd/es/form'
import { daysToSeconds, hoursToSeconds, secondsToDays, secondsToHours } from '@pushly/aqe/lib/utils'
import { RelativeDateDisplayMetric } from '@pushly/models/lib/enums/relative-date-display-metric'
import { CatalogItemType } from '@pushly/models/lib/enums/catalogs/index'

type ComparisonOperator = '>' | '<' | '>=' | '<=' | '==' | '!='

interface IDomainEcommEditor {
    domain: DomainDto
    visible: boolean
    onClose: (e: any) => void
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface IDomainEcommEditorFormValues {
    itemType?: CatalogItemType
    catalogLocales?: string[]
    catalogDefaultLocale?: string
    clickWindow?: { seconds: number; metric: string }
    viewWindow?: { seconds: number; metric: string }
    maxCartedItemAge?: { seconds: number; metric: string }
    browseAbandonmentEligibility?: { seconds: number; metric: string }
}

const validateNumericInput = async (
    _rule: Rule,
    value: number,
    operator: ComparisonOperator,
    compareValue: number,
    fieldName?: string,
) => {
    if (value == null || isNaN(value)) {
        return Promise.reject(`${fieldName || 'Field'} is required and must be a valid number.`)
    }

    if (!Number.isInteger(value)) {
        return Promise.reject(`${fieldName || 'Field'} must be a whole number.`)
    }

    let isValid = false
    let friendlyComparison = ''
    switch (operator) {
        case '>':
            isValid = value > compareValue
            friendlyComparison = 'greater than'
            break
        case '<':
            isValid = value < compareValue
            friendlyComparison = 'less than'
            break
        case '>=':
            isValid = value >= compareValue
            friendlyComparison = 'greater than or equal to'
            break
        case '<=':
            isValid = value <= compareValue
            friendlyComparison = 'less than or equal to'
            break
        case '==':
            isValid = value === compareValue
            friendlyComparison = 'equal to'
            break
        case '!=':
            isValid = value !== compareValue
            friendlyComparison = 'not equal to'
            break
        default:
            return Promise.reject(`Invalid comparison operator: ${operator}`)
    }

    if (!isValid) {
        return Promise.reject(`${fieldName || 'Field'} must be ${friendlyComparison} ${compareValue}.`)
    }

    return Promise.resolve()
}

const buildEcommConfigFromForm = (formValues: IDomainEcommEditorFormValues): IDomainEcommConfig => {
    return {
        item_type: formValues.itemType,
        click_attribution_window_hours: secondsToHours(formValues.clickWindow?.seconds),
        impression_attribution_window_hours: secondsToHours(formValues.viewWindow?.seconds),
        max_abandoned_item_age_days: secondsToDays(formValues.maxCartedItemAge?.seconds),
        ecomm_campaign_cooldown_period: {
            interval_seconds: formValues.browseAbandonmentEligibility?.seconds,
        },
        catalog_locales: formValues.catalogLocales,
        catalog_default_locale: formValues.catalogDefaultLocale,
    } as IDomainEcommConfig
}

const computeChangedDomainValues = (
    domain: DomainDto,
    formValues: IDomainEcommEditorFormValues,
): Partial<DomainDto> => {
    const values: Partial<DomainDto> = {}

    const builtConfig = buildEcommConfigFromForm(formValues)
    if (!deepEqual(builtConfig, domain.ecomm_config)) {
        values.ecomm_config = builtConfig
    }

    return values
}

export const DomainEcommEditor: React.FC<IDomainEcommEditor> = (props) => {
    const domainService = useMemo(() => Container.get(DomainService), [])

    const { domain, visible, onClose, onDomainUpdated } = props

    const prevEcommEnabledState = !!domain.ecomm_config
    const initialEcommConfig: IDomainEcommConfig = {
        item_type: domain.ecomm_config?.item_type ?? CatalogItemType.PRODUCT,
        click_attribution_window_hours: domain.ecomm_config?.click_attribution_window_hours ?? 24,
        impression_attribution_window_hours: domain.ecomm_config?.impression_attribution_window_hours ?? 0,
        max_abandoned_item_age_days: domain.ecomm_config?.max_abandoned_item_age_days ?? 7,
        ecomm_campaign_cooldown_period: domain.ecomm_config?.ecomm_campaign_cooldown_period ?? {
            interval_seconds: 86400,
        },
        catalog_locales: domain.ecomm_config?.catalog_locales ?? ['en-US'],
        catalog_default_locale: domain.ecomm_config?.catalog_default_locale ?? 'en-US',
    }

    const [ecommEnabled, setEcommEnabled] = useState<boolean>(prevEcommEnabledState)
    const [form] = Form.useForm<IDomainEcommEditorFormValues>()

    // Subscribe to value changes so the wrapping Drawer's disableSubmit recomputes as the user edits.
    Form.useWatch([], form)

    const ecommStateIsUnchanged = ecommEnabled === prevEcommEnabledState
    const currentValues = computeChangedDomainValues(domain, form.getFieldsValue())

    const handleSubmit = async () => {
        try {
            await form.validateFields()
            const values: Partial<DomainDto> = ecommEnabled
                ? computeChangedDomainValues(domain, form.getFieldsValue())
                : { ecomm_config: null }

            const { ok, data } = await domainService.patch(domain.id, values, {
                showLoadingScreen: true,
            })

            if (ok) {
                onDomainUpdated(data)
                onClose(null)
            }
        } catch (err) {
            // v6 validateFields rejects with { errorFields, values, outOfDate };
            // aqe's simpleFormErrorNotification handles both legacy and v6 shapes.
            simpleFormErrorNotification(err)
        }
    }

    const currentLocales: string[] = Form.useWatch('catalogLocales', form) ?? initialEcommConfig.catalog_locales ?? []

    return (
        <Drawer
            className={classnames('ddf-ecomm-settings-editor')}
            title="Update E-Commerce Settings"
            placement="right"
            closable={true}
            width={800}
            onSubmit={handleSubmit}
            disableSubmit={ecommStateIsUnchanged && Object.keys(currentValues).length === 0}
            onClose={onClose}
            open={visible}
        >
            <Form
                form={form}
                {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                className="hide-inline-errors"
                onFinish={handleSubmit}
                size="small"
                labelAlign="left"
                requiredMark={false}
            >
                <Well mode="ghost" hideHeader={true} footerMode="hidden" padding="none">
                    <Form.Item
                        label="Enable E-Commerce for this domain"
                        extra={
                            <span>
                                <InfoCircleOutlined /> Turn on e-commerce to configure attribution windows, item type,
                                and cart settings.
                            </span>
                        }
                    >
                        <Switch onChange={(value) => setEcommEnabled(value)} checked={ecommEnabled} />
                    </Form.Item>
                </Well>
                {ecommEnabled && (
                    <>
                        <Divider />

                        <Well title="General Settings" mode="ghost" footerMode="hidden" padding="none">
                            <Form.Item
                                label={
                                    <span>
                                        Item Type
                                        <Tooltip title="Choose the primary type of item that the client sells in their store.">
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </span>
                                }
                                name="itemType"
                                initialValue={initialEcommConfig.item_type}
                            >
                                <Select showSearch={false} optionFilterProp="title">
                                    <Select.Option key="product" value={CatalogItemType.PRODUCT}>
                                        Product
                                    </Select.Option>
                                    <Select.Option key="event" value={CatalogItemType.TICKETED_EVENT}>
                                        Ticketed Event
                                    </Select.Option>
                                    <Select.Option key="recipe" value={CatalogItemType.RECIPE}>
                                        Recipe
                                    </Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item
                                label={
                                    <span>
                                        Catalog Locales
                                        <Tooltip title="Used to match localized catalog fields (e.g., name, description) when building Item Group filters.">
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </span>
                                }
                                name="catalogLocales"
                                initialValue={initialEcommConfig.catalog_locales}
                            >
                                <Select
                                    mode="tags"
                                    tokenSeparators={[',', ' ', '\n']}
                                    placeholder="e.g. en-US, fr-CA"
                                    onChange={(value: string[]) => {
                                        const currentDefault =
                                            form.getFieldValue('catalogDefaultLocale') ||
                                            initialEcommConfig.catalog_default_locale
                                        const defaultRemoved = currentDefault && !value.includes(currentDefault)

                                        if (defaultRemoved) {
                                            form.setFieldsValue({
                                                catalogDefaultLocale: undefined,
                                            })
                                        }
                                    }}
                                />
                            </Form.Item>
                            <Form.Item
                                label={
                                    <span>
                                        Default Catalog Locale
                                        <Tooltip title="Used to choose which locale to display for catalog item fields in the UI.">
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </span>
                                }
                                name="catalogDefaultLocale"
                                initialValue={initialEcommConfig.catalog_default_locale}
                                rules={[
                                    {
                                        validator: (_rule: Rule, value: string) => {
                                            const locales = currentLocales || []
                                            if (locales.length > 0 && !value) {
                                                return Promise.reject('A default locale is required.')
                                            }
                                            if (value && locales.length > 0 && !locales.includes(value)) {
                                                return Promise.reject(
                                                    'Default locale must be one of the configured catalog locales.',
                                                )
                                            }
                                            return Promise.resolve()
                                        },
                                    },
                                ]}
                            >
                                <Select placeholder="Select default locale">
                                    {currentLocales.map((locale: string) => (
                                        <Select.Option key={locale} value={locale}>
                                            {locale}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item
                                label={
                                    <span>
                                        Click Attribution Window
                                        <Tooltip title="How long after a user clicks a notification should a conversion still be attributed to that click?">
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </span>
                                }
                                name="clickWindow"
                                initialValue={{
                                    seconds: hoursToSeconds(initialEcommConfig.click_attribution_window_hours),
                                    metric: 'hours',
                                }}
                                rules={[
                                    {
                                        required: true,
                                        validator: (_rule: Rule, value: { seconds: number; metric: string }) =>
                                            validateNumericInput(
                                                _rule,
                                                secondsToHours(value?.seconds),
                                                '>',
                                                0,
                                                'Click Attribution Window',
                                            ),
                                    },
                                ]}
                            >
                                <SimpleTimeframe disabledMetrics={['seconds', 'minutes', 'days']} />
                            </Form.Item>

                            <Form.Item
                                label={
                                    <span>
                                        View Attribution Window
                                        <Tooltip title="How long after a user views a notification (without clicking) should a conversion be attributed? Set to 0 to disable.">
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </span>
                                }
                                name="viewWindow"
                                initialValue={{
                                    seconds: hoursToSeconds(initialEcommConfig.impression_attribution_window_hours),
                                    metric: 'hours',
                                }}
                                rules={[
                                    {
                                        required: true,
                                        validator: (_rule: Rule, value: { seconds: number; metric: string }) =>
                                            validateNumericInput(
                                                _rule,
                                                secondsToHours(value?.seconds),
                                                '>=',
                                                0,
                                                'View Attribution Window',
                                            ),
                                    },
                                ]}
                            >
                                <SimpleTimeframe disabledMetrics={['seconds', 'minutes', 'days']} />
                            </Form.Item>
                            <Form.Item
                                label={
                                    <span>
                                        Max Carted Item Age
                                        <Tooltip title="Carted items older than the specified number of days are excluded from abandonment campaigns. Can be overridden on a per campaign basis.">
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </span>
                                }
                                name="maxCartedItemAge"
                                initialValue={{
                                    seconds: daysToSeconds(initialEcommConfig.max_abandoned_item_age_days),
                                    metric: 'days',
                                }}
                                rules={[
                                    {
                                        required: true,
                                        validator: (_rule: Rule, value: { seconds: number; metric: string }) =>
                                            validateNumericInput(
                                                _rule,
                                                secondsToDays(value?.seconds),
                                                '>=',
                                                1,
                                                'Max Carted Item Age',
                                            ),
                                    },
                                ]}
                            >
                                <SimpleTimeframe disabledMetrics={['seconds', 'minutes', 'hours']} />
                            </Form.Item>
                            <Form.Item
                                label={
                                    <span>
                                        Browse Abandonment Eligibility
                                        <Tooltip title="Prevents a subscriber from entering a browse abandonment campaign immediately after participating in a cart abandonment campaign.">
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </span>
                                }
                                name="browseAbandonmentEligibility"
                                initialValue={{
                                    seconds: initialEcommConfig.ecomm_campaign_cooldown_period.interval_seconds,
                                    metric: 'days',
                                }}
                                rules={[
                                    {
                                        required: true,
                                        validator: (_rule: Rule, value: { seconds: number; metric: string }) =>
                                            validateNumericInput(
                                                _rule,
                                                secondsToDays(value?.seconds),
                                                '>=',
                                                0,
                                                'Browse Abandonment Eligibility',
                                            ),
                                    },
                                ]}
                            >
                                <SimpleTimeframe disabledMetrics={['seconds', 'minutes', 'hours']} />
                            </Form.Item>
                        </Well>
                    </>
                )}
            </Form>
        </Drawer>
    )
}

interface IDomainEcommWell {
    loading?: boolean
    domain: DomainDto
    title?: string
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface IDEWState {
    showDrawer: boolean
    drawerKey?: string
}

export class DomainEcommWell extends React.Component<IDomainEcommWell, IDEWState> {
    public state: IDEWState = {
        showDrawer: false,
    }

    protected readonly appState: AppState

    public constructor(props: IDomainEcommWell) {
        super(props)

        this.appState = Container.get(AppState)
    }

    public render() {
        return (
            <Well
                className="type-3 nested"
                title="E-Commerce Settings"
                actions={
                    this.appState.abilityStore.can(AbilityAction.UPDATE, asDomainSubject(this.props.domain)) && (
                        <Button size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                            <EditOutlined />
                            <span>Edit</span>
                        </Button>
                    )
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            {this.renderEcommConfig()}

                            <DomainEcommEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                domain={this.domain}
                                onClose={this.handleDrawerClose}
                                onDomainUpdated={this.props.onDomainUpdated}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected renderEcommConfig() {
        const config = this.domain?.ecomm_config

        return !config ? (
            <Well mode="ghost" hideHeader={true} hideFooter={true}>
                <FormLayoutRow
                    label="Status"
                    value={<StatusBadge type="generic" expanded={true} status={'DISABLED'} />}
                />
            </Well>
        ) : (
            <Well mode="ghost" hideHeader={true} hideFooter={true}>
                <FormLayoutRow
                    label="Status"
                    value={<StatusBadge type="generic" expanded={true} status={'ENABLED'} />}
                />
                <FormLayoutRow label="Item Type" value={titleCase(config.item_type)} />
                <FormLayoutRow
                    label="Click Attribution"
                    value={`${this.renderDaysAndHours(config.click_attribution_window_hours ?? 24)}`}
                />
                <FormLayoutRow
                    label="View Attribution"
                    value={`${
                        !config.impression_attribution_window_hours
                            ? 'Disabled'
                            : this.renderDaysAndHours(config.impression_attribution_window_hours)
                    }`}
                />
                <FormLayoutRow
                    label="Max Abandoned Item Age"
                    value={`${this.renderFriendlyMetric(
                        config.max_abandoned_item_age_days ?? 7,
                        RelativeDateDisplayMetric.DAYS,
                    )}`}
                />
                <FormLayoutRow
                    label="Browse Abandonment Eligibility"
                    value={`${this.renderFriendlyMetric(
                        secondsToDays(config.ecomm_campaign_cooldown_period.interval_seconds),
                        RelativeDateDisplayMetric.DAYS,
                    )}`}
                />
                <FormLayoutRow label="Catalog Locales" value={config.catalog_locales?.join(', ') || 'en-US'} />
                <FormLayoutRow label="Default Locale" value={config.catalog_default_locale || 'en-US'} />
            </Well>
        )
    }

    protected get domain(): DomainDto {
        return this.props.domain
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }

    protected renderFriendlyMetric = (
        value: number,
        metric: RelativeDateDisplayMetric.HOURS | RelativeDateDisplayMetric.DAYS,
    ) => {
        switch (metric) {
            case RelativeDateDisplayMetric.HOURS:
                return value === 1 ? `${value} hour` : `${value} hours`
            case RelativeDateDisplayMetric.DAYS:
                return value === 1 ? `${value} day` : `${value} days`
        }
    }

    protected renderDaysAndHours = (hours: number) => {
        if (hours >= 24) {
            const days = Math.floor(hours / 24)
            const remainingHours = hours % 24

            if (remainingHours === 0) {
                return `${this.renderFriendlyMetric(days, RelativeDateDisplayMetric.DAYS)}`
            } else {
                return `${this.renderFriendlyMetric(days, RelativeDateDisplayMetric.DAYS)}, ${this.renderFriendlyMetric(
                    remainingHours,
                    RelativeDateDisplayMetric.HOURS,
                )}`
            }
        }

        return `${this.renderFriendlyMetric(hours, RelativeDateDisplayMetric.HOURS)}`
    }
}
